import { useCallback, useEffect, useState } from 'react';
import { Bell, CheckCheck, Clock } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { apiGet, apiPost } from '@/lib/api';
import { Link } from 'react-router-dom';
import { buddyEvents } from '@/components/TutorBuddy';

const KIND_TONE = {
  syllabus_added: 'bg-indigo-50 text-indigo-700',
  syllabus_ready: 'bg-emerald-50 text-emerald-700',
  plan_risk_orange: 'bg-amber-50 text-amber-800',
  plan_risk_red: 'bg-rose-50 text-rose-700',
};

/** Bell beside the profile avatar. Admins also see operational nudges. */
export function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    apiGet('/notifications').then(d => {
      setItems(d.items || []); setUnread(d.unread || 0);
      const ready = (d.items || []).find(n => n.kind === 'school_material_ready' && !n.read && !sessionStorage.getItem(`gv_notified_${n.notif_id}`));
      if (ready) { buddyEvents.say(ready.title, 'happy', 14000, 'school-materials'); sessionStorage.setItem(`gv_notified_${ready.notif_id}`, '1'); }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 120000);
    return () => clearInterval(t);
  }, [load]);

  const markAll = async () => {
    try { await apiPost('/notifications/read', { all: true }); setUnread(0); load(); } catch { /* silent */ }
  };

  return (
    <DropdownMenu open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notification-bell" title="Notifications"
          className="relative p-2 rounded-lg hover:bg-slate-50 transition-colors text-slate-500">
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span data-testid="notification-unread-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-heading flex items-center justify-center">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-xl w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
          <p className="font-heading text-sm font-semibold text-slate-800">Notifications</p>
          {items.length > 0 && (
            <button data-testid="notification-mark-all" onClick={markAll}
              className="text-[11px] font-body text-slate-400 hover:text-slate-600 flex items-center gap-1">
              <CheckCheck className="w-3 h-3" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 && (
            <p className="px-3 py-6 text-center text-[12px] text-slate-400 font-body" data-testid="notification-empty">
              Nothing new right now.
            </p>
          )}
          {items.map(n => (
            <div key={n.notif_id} data-testid={`notification-${n.notif_id}`}
              className={`px-3 py-2.5 border-b border-slate-50 ${n.read ? 'opacity-60' : ''}`}>
              <div className="flex items-start gap-2">
                <span className={`mt-0.5 text-[9px] font-heading uppercase px-1.5 py-0.5 rounded-full ${KIND_TONE[n.kind] || 'bg-slate-100 text-slate-600'}`}>
                  {(n.kind || 'note').split('_')[0]}
                </span>
                <div className="min-w-0">
                  <p className="font-heading text-[12px] font-semibold text-slate-800">{n.title}</p>
                  <p className="text-[11px] text-slate-500 font-body leading-relaxed mt-0.5">{n.body}</p>
                  {['/school-materials', '/staff?tab=syllabus_library', '/support?tab=syllabus_library'].includes(n.meta?.url) && <Link data-testid={`notification-open-${n.notif_id}`} to={n.meta.url} onClick={() => setOpen(false)} className="inline-block mt-1 text-xs text-sky-700 underline">{n.meta.url === '/school-materials' ? 'Open school materials' : 'Review syllabus'}</Link>}
                  {n.hours_left !== null && n.hours_left !== undefined && (
                    <p className="text-[10px] text-amber-700 font-body mt-1 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {n.hours_left} hrs left on the 48-hour build
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default NotificationBell;
